import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./shared";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT_EMAIL } from "@/i18n/contactDetails";

const CONTACT_IMG =
    "https://images.unsplash.com/photo-1625850864219-585a0e8fac0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const EMPTY = { name: "", email: "", company: "", message: "", website: "" };

const Field = ({ label, testid, ...props }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.3em] text-ash">{label}</span>
        <input
            data-testid={testid}
            {...props}
            className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none"
        />
    </label>
);

export const Contact = () => {
    const { t } = useLanguage();
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
            toast.success(t.contact.success);
            setForm(EMPTY);
        } catch {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative overflow-hidden py-32 md:py-44"
        >
            <img
                src={CONTACT_IMG}
                alt="A professional team at work in moody light"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/85" />
            <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />

            <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-12">
                <div className="md:col-span-6">
                    <Reveal>
                        <SectionTag index="№ 05" label={t.contact.tag} />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-10 font-display text-5xl leading-[1.02] tracking-tight md:text-6xl lg:text-7xl">
                            {t.contact.headingA}{" "}
                            <span className="italic text-gold">{t.contact.headingB}</span>.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-md text-base leading-relaxed text-ash">
                            {t.contact.sub}
                        </p>
                        <p className="mt-6 max-w-md text-sm leading-relaxed text-ash/70">
                            {t.contact.note}
                        </p>
                        <div className="mt-8 space-y-4">
                            <a
                                data-testid="contact-email-link"
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="flex w-fit items-center gap-3 text-base text-bone transition-colors hover:text-gold"
                            >
                                <Mail className="h-4 w-4 text-gold" />
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </Reveal>
                </div>
                <div className="md:col-span-6">
                    <Reveal delay={0.15}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="border border-white/10 bg-black/60 p-8 backdrop-blur-xl md:p-10"
                        >
                            <input
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={set("website")}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                            />
                            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                                <Field
                                    label={t.contact.nameLabel}
                                    testid="contact-form-name-input"
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder={t.contact.namePlaceholder}
                                    required
                                    maxLength={120}
                                />
                                <Field
                                    label={t.contact.emailLabel}
                                    testid="contact-form-email-input"
                                    type="email"
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder={t.contact.emailPlaceholder}
                                    required
                                    maxLength={200}
                                />
                            </div>
                            <div className="mt-8">
                                <Field
                                    label={t.contact.companyLabel}
                                    testid="contact-form-company-input"
                                    value={form.company}
                                    onChange={set("company")}
                                    placeholder={t.contact.companyPlaceholder}
                                    maxLength={200}
                                />
                            </div>
                            <label className="mt-8 block">
                                <span className="text-[11px] uppercase tracking-[0.3em] text-ash">
                                    {t.contact.messageLabel}
                                </span>
                                <textarea
                                    data-testid="contact-form-message-input"
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder={t.contact.messagePlaceholder}
                                    required
                                    maxLength={4000}
                                    rows={4}
                                    className="mt-3 w-full resize-none border-b border-white/20 bg-transparent pb-3 text-base text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none"
                                />
                            </label>
                            <button
                                data-testid="contact-form-submit-button"
                                type="submit"
                                disabled={sending}
                                className="group mt-10 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {sending ? t.contact.sending : t.contact.submit}
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
